import { ref, watch, onBeforeUnmount } from 'vue'
import { getSubjectBasicInfo, patchSubjectBasicInfo } from './cohortApi.js'

// All requests capture org + frozen subject + report date; never infer identity from a device.
export function useSubjectBasicInfo(context, api = { get: getSubjectBasicInfo, patch: patchSubjectBasicInfo }) {
  const profile = ref(null), loading = ref(false), saving = ref(false), error = ref('')
  let revision = 0, disposed = false
  const key = c => JSON.stringify([c.org, c.subjectId, c.asOf || ''])
  const current = (request, c) => !disposed && revision === request && key(context()) === key(c)
  function message(e) { return e.response?.data?.detail || e.message || 'cohort_profile_load_failed' }
  async function refresh() {
    const c = { ...context() }, request = ++revision
    profile.value = null; error.value = ''; loading.value = false
    if (!c.org || !c.subjectId) return null
    loading.value = true
    try {
      const dto = await api.get(c.org, c.subjectId, c.asOf)
      if (!current(request, c)) return null
      profile.value = dto
      return dto
    } catch (e) { if (current(request, c)) error.value = message(e); return null }
    finally { if (current(request, c)) loading.value = false }
  }
  async function save(patch) {
    if (saving.value || loading.value || !Object.keys(patch).length) return null
    const c = { ...context() }, request = ++revision
    if (!c.org || !c.subjectId) return null
    saving.value = true; error.value = ''
    try {
      await api.patch(c.org, c.subjectId, patch)
      // PATCH age is today; always re-read using the report's asOf date.
      if (!current(request, c)) return null
      const dto = await api.get(c.org, c.subjectId, c.asOf)
      if (!current(request, c)) return null
      profile.value = dto
      return dto
    } catch (e) { if (current(request, c)) error.value = message(e); return null }
    finally { if (current(request, c)) saving.value = false }
  }
  watch(() => key(context()), () => { saving.value = false; refresh() }, { immediate: true })
  onBeforeUnmount(() => { disposed = true; revision++ })
  return { profile, loading, saving, error, refresh, save }
}
